import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, MapPin, Search, Sparkles, Play, Film, ChevronRight, Loader2, Plus, Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';
import { TierBadge } from '../components/TierBadge';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { getProfileUrl } from '../utils/profileUtils';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

function ClipCard({ clip }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [error, setError] = useState(false);

  const videoUrl = clip.video_url
    ? clip.video_url.startsWith('http') ? clip.video_url : `${API_BASE_URL}${clip.video_url}`
    : null;

  const handleMouseEnter = () => {
    if (videoRef.current && videoUrl && !error) {
      videoRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
  };
  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setPlaying(false);
    }
  };

  return (
    <Link to={`/reels/${clip.reel_id}`}>
      <Card
        className="group relative overflow-hidden border-border/40 hover:border-border/80 transition-all"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid={`clip-card-${clip.reel_id}`}
      >
        <div className="aspect-[9/16] bg-black relative">
          {videoUrl && !error ? (
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full object-cover"
              muted={muted}
              loop
              playsInline
              preload="metadata"
              onError={() => setError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted/30">
              <Film className="w-8 h-8 text-muted-foreground/30" />
            </div>
          )}

          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

          {/* Play icon */}
          {!playing && !error && (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Play className="w-5 h-5 text-white fill-white" />
              </div>
            </div>
          )}

          {/* Mute toggle */}
          {playing && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMuted(!muted); }}
              className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/40 flex items-center justify-center"
            >
              {muted ? <VolumeX className="w-3.5 h-3.5 text-white" /> : <Volume2 className="w-3.5 h-3.5 text-white" />}
            </button>
          )}

          {/* Bottom info */}
          <div className="absolute bottom-0 left-0 right-0 p-2.5">
            <div className="flex items-center gap-2 mb-1">
              {clip.avatar ? (
                <img src={clip.avatar} alt="" className="w-6 h-6 rounded-full object-cover border border-white/20" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-muted/50 flex items-center justify-center text-[10px] font-bold text-white/60">
                  {(clip.name || '?')[0]}
                </div>
              )}
              <span className="text-xs font-medium text-white/90 truncate">@{clip.handle || 'user'}</span>
            </div>
            {clip.caption && (
              <p className="text-[10px] text-white/60 line-clamp-2 leading-tight">{clip.caption}</p>
            )}
          </div>

          {/* Stats */}
          <div className="absolute top-2 left-2 flex items-center gap-1.5">
            <Badge className="bg-black/40 text-white/80 text-[9px] px-1.5 py-0.5 border-0">
              <Play className="w-2.5 h-2.5 mr-0.5" /> {clip.views || 0}
            </Badge>
          </div>
        </div>
      </Card>
    </Link>
  );
}


export default function ArtistsPage() {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [clips, setClips] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clipsLoading, setClipsLoading] = useState(true);
  const [filters, setFilters] = useState({ neighborhood: '', genre: '', search: '' });

  useEffect(() => {
    fetchReferenceData();
    fetchClips();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchArtists(), 300);
    return () => clearTimeout(t);
  }, [filters.neighborhood, filters.genre, filters.search]);

  const fetchReferenceData = async () => {
    try {
      const [hoodRes, genreRes] = await Promise.all([
        fetch(`${API}/reference/neighborhoods`),
        fetch(`${API}/reference/genres`)
      ]);
      if (hoodRes.ok) setNeighborhoods((await hoodRes.json()).neighborhoods || []);
      if (genreRes.ok) setGenres((await genreRes.json()).genres || []);
    } catch {}
  };

  const fetchClips = async () => {
    try {
      setClipsLoading(true);
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const res = await fetch(`${API}/reels?limit=8`, { headers });
      if (res.ok) {
        const data = await res.json();
        setClips((data.reels || []).filter(r => r.video_url));
      }
    } catch (e) {
      console.error('[ArtistsPage] Clips fetch error:', e);
    } finally {
      setClipsLoading(false);
    }
  };

  const fetchArtists = async () => {
    try {
      setLoading(true);
      const headers = isAuthenticated ? getAuthHeaders() : {};
      let url = `${API}/discover/creators?limit=100&role=artist`;
      if (filters.neighborhood) url += `&neighborhood=${encodeURIComponent(filters.neighborhood)}`;
      if (filters.genre) url += `&genre=${encodeURIComponent(filters.genre)}`;
      if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;

      const res = await fetch(url, { headers });

      if (res.ok) {
        const result = await safeJson(res);
        const data = result.data;
        if (data?.creators) {
          setArtists(data.creators.map(c => ({
            artist_id: c.artist_id || c.user_id,
            user_id: c.user_id,
            handle: c.handle,
            stage_name: c.stage_name || c.name || 'Unknown',
            avatar_url: c.avatar_url || c.picture,
            neighborhood: c.neighborhood,
            genres: c.genres || [],
            tier: c.tier || 'starter',
            is_founder: c.is_founder || false,
            subscription_status: c.subscription_status,
            followers_count: c.followers_count || 0,
            profile_complete: c.is_profile_complete,
            role: c.role,
            role_display: c.role_display,
          })));
        } else {
          setArtists([]);
        }
      } else {
        // Fallback
        const fallbackRes = await fetch(`${API}/artists?include_new=true`, { headers });
        if (fallbackRes.ok) {
          const fbResult = await safeJson(fallbackRes);
          setArtists(fbResult.data || []);
        }
      }
    } catch (e) {
      console.error('[ArtistsPage] Error:', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredArtists = filters.search
    ? artists.filter(a => {
        const s = filters.search.toLowerCase();
        return (a.stage_name?.toLowerCase().includes(s) || a.handle?.toLowerCase().includes(s));
      })
    : artists;

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="artists-page">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-2">Discover Artists</h1>
          <p className="text-muted-foreground">Find and follow talented artists from the DMV</p>
        </div>

        {/* ── Soundstage Clips ── */}
        <section className="mb-10" data-testid="clips-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Film className="w-5 h-5 text-violet-400" />
              <h2 className="text-lg font-bold">Soundstage Clips</h2>
            </div>
            <Link to="/reels" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {clipsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : clips.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3" data-testid="clips-grid">
              {clips.map((clip) => (
                <ClipCard key={clip.reel_id} clip={clip} />
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center border-dashed border-border/40 bg-card/30" data-testid="clips-empty">
              <Film className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground mb-3">No Clips Yet</p>
              {isAuthenticated && (
                <Button variant="outline" size="sm" onClick={() => navigate('/reels/create')} data-testid="create-clip-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Post a Clip
                </Button>
              )}
            </Card>
          )}
        </section>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search artists..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10 bg-muted/50 border-border"
              data-testid="search-artists-input"
            />
          </div>
          <Select value={filters.neighborhood} onValueChange={(v) => setFilters({ ...filters, neighborhood: v })}>
            <SelectTrigger className="w-full sm:w-48 bg-muted/50 border-border" data-testid="neighborhood-filter">
              <SelectValue placeholder="Neighborhood" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Neighborhoods</SelectItem>
              {neighborhoods.map((hood) => (
                <SelectItem key={hood} value={hood}>{hood}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.genre} onValueChange={(v) => setFilters({ ...filters, genre: v })}>
            <SelectTrigger className="w-full sm:w-48 bg-muted/50 border-border" data-testid="genre-filter">
              <SelectValue placeholder="Genre" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Genres</SelectItem>
              {genres.map((genre) => (
                <SelectItem key={genre} value={genre}>{genre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Artists Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredArtists.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-testid="artists-grid">
            {filteredArtists.map((artist, index) => (
              <motion.div
                key={artist.artist_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <Link to={artist.handle ? getProfileUrl({ handle: artist.handle }) : `/artist/${artist.artist_id}`}>
                  <Card className="glass-card border-border overflow-hidden hover-lift group">
                    <div className="aspect-square bg-muted relative">
                      {artist.avatar_url ? (
                        <img src={artist.avatar_url} alt={artist.stage_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center">
                          <span className="text-3xl font-heading font-bold opacity-50">{(artist.stage_name || '?')[0]}</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                      {!artist.profile_complete && (
                        <div className="absolute top-2 left-2">
                          <Badge className="bg-secondary/90 text-foreground text-xs gap-1">
                            <Sparkles className="w-3 h-3" /> New
                          </Badge>
                        </div>
                      )}

                      <div className="absolute top-2 right-2">
                        <TierBadge
                          tier={artist.tier}
                          subscriptionStatus={artist.subscription_status}
                          isFounder={artist.is_founder}
                          size="sm"
                        />
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="font-medium truncate">{artist.stage_name}</p>
                      {artist.handle && (
                        <p className="text-xs text-muted-foreground">{artist.handle ? `@${artist.handle}` : ""}</p>
                      )}
                      {artist.neighborhood && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" /> {artist.neighborhood}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Users className="w-3 h-3" /> {artist.followers_count?.toLocaleString() || 0}
                      </p>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-muted-foreground" data-testid="empty-artists-state">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No Artists Yet</p>
            <p className="text-sm mt-2">Be the first to join as an artist!</p>
            <p className="text-xs mt-4 opacity-70">
              Only users with the Artist role appear here.<br />
              Podcasters, Streamers, and other creators have their own sections.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
